import React from 'react'

import { useAppStore } from '@runtime/core/store/appStore'
import type { I18nT } from '@runtime/core/i18n/i18n'
import { applyPosterThemeSelection } from '@runtime/core/theme/applyPosterThemeSelection'
import { commitPosterThemeSelection } from '@runtime/core/theme/commitPosterThemeSelection'
import { requestPosterThemeAccess } from '@runtime/core/theme/posterThemeAccessApi'
import {
  DEFAULT_POSTER_THEME_ID,
  loadThemesIndex,
  precacheThemeAssets,
  resolveTheme,
} from '@runtime/core/theme/posterThemes'
import {
  canAccessPosterTheme,
  getPosterThemeAccessToken,
  isDynamicPosterPrivate,
  isPosterAdmin,
  isPosterPrivate,
  isPosterPrivateUnlocked,
  setPosterThemeAccessToken,
} from '@runtime/core/theme/posterAccess'
import type { ThemeIndexItem } from '@runtime/features/poster/logic/posterTheme.types'

type PosterThemePickerArgs = {
  open: boolean
  onClose: () => void
  t: I18nT
  user: Record<string, unknown> | null
  setThemeId: (themeId: string) => void
}

function prioritizeDefaultPosterTheme(
  items: ThemeIndexItem[],
): ThemeIndexItem[] {
  const defaultItems: ThemeIndexItem[] = []
  const otherItems: ThemeIndexItem[] = []

  items.forEach((item) => {
    if (item.id === DEFAULT_POSTER_THEME_ID) {
      defaultItems.push(item)
      return
    }

    otherItems.push(item)
  })

  return [...defaultItems, ...otherItems]
}

export function usePosterThemePicker({
  open,
  onClose,
  t,
  user,
  setThemeId,
}: PosterThemePickerArgs) {
  const [list, setList] = React.useState<ThemeIndexItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [applying, setApplying] = React.useState(false)
  const [loadedMap, setLoadedMap] = React.useState<Record<string, boolean>>({})
  const [showPrivatePrompt, setShowPrivatePrompt] = React.useState(false)
  const [privatePassword, setPrivatePassword] = React.useState('')
  const [privateError, setPrivateError] = React.useState('')
  const authToken = useAppStore((s) => s.authToken)
  const privateUnlockedAll = useAppStore((s) => s.posterPrivateUnlockedAll)
  const privateUnlockedIds = useAppStore((s) => s.posterPrivateUnlockedIds)
  const addPosterPrivateUnlockedId = useAppStore(
    (s) => s.addPosterPrivateUnlockedId,
  )

  const isAdmin = isPosterAdmin(user)
  const isPrivateUnlocked =
    isAdmin ||
    privateUnlockedAll ||
    list.some((item) => isPosterPrivateUnlocked(item.id))

  React.useEffect(() => {
    let mounted = true

    async function run() {
      setLoading(true)
      try {
        const items = await loadThemesIndex()
        if (mounted) setList(items)
      } catch (error) {
        if (mounted) setList([])
        try {
          console.warn('[PosterModal] loadThemesIndex failed:', error)
        } catch {
          // ignore
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }

    if (open) {
      setList([])
      setLoadedMap({})
      run()
    }

    return () => {
      mounted = false
    }
  }, [open])

  const markLoaded = React.useCallback((id: string) => {
    setLoadedMap((previous) =>
      previous[id] ? previous : { ...previous, [id]: true },
    )
  }, [])

  const revealDynamicTheme = React.useCallback(
    async (themeId: string, accessToken: string) => {
      setPosterThemeAccessToken(themeId, accessToken)
      addPosterPrivateUnlockedId(themeId)

      const cfg = await resolveTheme(themeId).catch(() => null)
      if (!cfg) return

      const activeThemeId = useAppStore.getState().themeId
      if (activeThemeId === themeId) {
        await precacheThemeAssets(themeId, cfg).catch(() => undefined)
        commitPosterThemeSelection({ themeId, cfg, setThemeId })
      }

      if (!cfg.preview) return

      setList((current) =>
        current.map((item) =>
          item.id === themeId ? { ...item, preview: cfg.preview } : item,
        ),
      )
    },
    [addPosterPrivateUnlockedId, setThemeId],
  )

  React.useEffect(() => {
    let cancelled = false

    async function hydrateDynamicPrivateThemes() {
      if (!open || !authToken || !list.length) return

      const candidates = list.filter(
        (item) =>
          isDynamicPosterPrivate(item.id) &&
          item.protected &&
          !getPosterThemeAccessToken(item.id),
      )
      if (!candidates.length) return

      const granted = await Promise.all(
        candidates.map(async (item) => {
          const accessToken = await Promise.resolve()
            .then(() =>
              requestPosterThemeAccess({
                themeId: item.id,
                authToken,
              }),
            )
            .catch(() => null)

          return accessToken ? { themeId: item.id, accessToken } : null
        }),
      )

      if (cancelled) return

      await Promise.all(
        granted
          .filter(
            (entry): entry is { themeId: string; accessToken: string } =>
              !!entry,
          )
          .map((entry) => revealDynamicTheme(entry.themeId, entry.accessToken)),
      )
    }

    void hydrateDynamicPrivateThemes()

    return () => {
      cancelled = true
    }
  }, [authToken, list, open, revealDynamicTheme])

  const normalizePosterPassword = React.useCallback((value: string) => {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }, [])

  const isPrivateVisible = React.useCallback(
    (id: string) => {
      if (!isPosterPrivate(id)) return false
      if (isAdmin) return true
      if (privateUnlockedAll && !isDynamicPosterPrivate(id)) return true
      return privateUnlockedIds.includes(id) || isPosterPrivateUnlocked(id)
    },
    [isAdmin, privateUnlockedAll, privateUnlockedIds],
  )

  const visibleList = React.useMemo(
    () =>
      prioritizeDefaultPosterTheme(
        list.filter(
          (item) => !isPosterPrivate(item.id) || isPrivateVisible(item.id),
        ),
      ),
    [list, isPrivateVisible],
  )

  const openPrivatePrompt = React.useCallback(() => {
    setPrivatePassword('')
    setPrivateError('')
    setShowPrivatePrompt(true)
  }, [])

  const closePrivatePrompt = React.useCallback(() => {
    setShowPrivatePrompt(false)
  }, [])

  const handlePrivatePasswordChange = React.useCallback((value: string) => {
    setPrivatePassword(value)
  }, [])

  const handlePrivateSubmit = React.useCallback(async () => {
    const entered = normalizePosterPassword(privatePassword)
    if (!entered) {
      setPrivateError(t('posterModal.privateError'))
      return
    }

    const privateItems = list.filter((item) => isPosterPrivate(item.id))
    const grantedMatches = await Promise.all(
      privateItems.map(async (item) => {
        const accessToken = await requestPosterThemeAccess({
          themeId: item.id,
          password: privatePassword,
          authToken,
        }).catch(() => null)

        return accessToken ? { themeId: item.id, accessToken } : null
      }),
    )

    const grantedPrivateMatches = grantedMatches.filter(
      (entry): entry is { themeId: string; accessToken: string } => !!entry,
    )

    await Promise.all(
      grantedPrivateMatches.map((entry) =>
        revealDynamicTheme(entry.themeId, entry.accessToken),
      ),
    )

    if (!grantedPrivateMatches.length) {
      setPrivateError(t('posterModal.privateError'))
      return
    }

    setPrivatePassword('')
    setPrivateError('')
    setShowPrivatePrompt(false)
  }, [
    authToken,
    list,
    normalizePosterPassword,
    privatePassword,
    revealDynamicTheme,
    t,
  ])

  const onPick = React.useCallback(
    async (item: ThemeIndexItem) => {
      if (applying) return

      try {
        setApplying(true)
        if (!canAccessPosterTheme(item.id, user)) {
          alert(t('posterModal.privateDenied'))
          return
        }

        await applyPosterThemeSelection({ themeId: item.id, setThemeId })
        onClose()
      } catch (error) {
        try {
          console.warn('[PosterModal] resolve/apply failed:', error)
        } catch {
          // ignore
        }
        alert(t('posterModal.loadError'))
      } finally {
        setApplying(false)
      }
    },
    [applying, onClose, setThemeId, t, user],
  )

  return {
    applying,
    handlePrivatePasswordChange,
    handlePrivateSubmit,
    isPrivateUnlocked,
    isPrivateVisible,
    list,
    loadedMap,
    loading,
    markLoaded,
    onPick,
    openPrivatePrompt,
    privateError,
    privatePassword,
    showPrivatePrompt,
    visibleList,
    closePrivatePrompt,
  }
}
